import { z } from "zod";
import { httpLinkSchema } from "./links";
import { WHO_ACTS } from "./whoActs";

/**
 * skyMonitor's shared contract (docs/research/2026-09-28-skymonitor-spec.md,
 * sections 7, 9 and 10): the closed lists, the words the screens and the messages use, the
 * parameters of each check kind, the facts a `monitor` notification carries, and the route views
 * and bodies.
 *
 * Pure and dependency-free beyond zod and `whoActs.ts` (#829: a `failed_run` alert's words), so the server, the client, the runner (which renders the
 * alerts) and skyMonitor read one list. This file never imports `notifications.ts`: that file
 * imports the facts schema from here.
 *
 * SEC-17: nothing here carries a row value. A check's observations are counts, hours and minutes;
 * a table's name stays in the instance schema and never reaches a notification.
 */

/** The platform feature flag (`features.flags.monitor`): off hides the screens and idles skyMonitor. */
export const MONITOR_FLAG = "monitor";

/** Every kind the rule model knows. Phase 1 evaluates the first five (`EVALUATED_CHECK_KINDS`). */
export const CHECK_KINDS = [
  "freshness",
  "overdue_run",
  "zero_rows",
  "volume",
  "duration",
  "schema_change",
  "table_missing",
  // Reliability 1.4 (#829, `docs/requirements/slo-canary.md` R6.4, instance 0034).
  "failed_run",
] as const;
export type CheckKind = (typeof CHECK_KINDS)[number];

/** The kinds built from run history alone (phase 1): no warehouse query, no credential. */
export const EVALUATED_CHECK_KINDS = [
  "freshness",
  "overdue_run",
  "zero_rows",
  "volume",
  "duration",
  "failed_run",
] as const satisfies readonly CheckKind[];
export type EvaluatedCheckKind = (typeof EVALUATED_CHECK_KINDS)[number];

export function isEvaluatedKind(kind: CheckKind): kind is EvaluatedCheckKind {
  return (EVALUATED_CHECK_KINDS as readonly CheckKind[]).includes(kind);
}

/** What a check is about: one loaded table, or one schedule. */
export const CHECK_SCOPES = ["table", "schedule"] as const;
export type CheckScope = (typeof CHECK_SCOPES)[number];

export const CHECK_SCOPE_OF = {
  freshness: "table",
  overdue_run: "schedule",
  zero_rows: "table",
  volume: "table",
  duration: "schedule",
  schema_change: "table",
  table_missing: "table",
  failed_run: "schedule",
} as const satisfies Record<CheckKind, CheckScope>;

export const CHECK_SEVERITIES = ["warning", "failure"] as const;
export type CheckSeverity = (typeof CHECK_SEVERITIES)[number];

/** The default severity of each kind (spec section 2). */
export const CHECK_DEFAULT_SEVERITY = {
  freshness: "failure",
  overdue_run: "failure",
  zero_rows: "warning",
  volume: "warning",
  duration: "warning",
  schema_change: "warning",
  table_missing: "failure",
  failed_run: "failure",
} as const satisfies Record<CheckKind, CheckSeverity>;

/** A default check is skyMonitor's; an edited one is the admin's and discovery never rewrites it. */
export const CHECK_SOURCES = ["default", "custom"] as const;

/**
 * What one evaluation found. `breach` opens or keeps an incident; `training` means too little
 * history to judge; `paused` a table whose every schedule is paused and that is still inside its
 * limit; `unknown` a check with nothing to judge (no schedule, no load yet). Only `pass` and
 * `paused` close an incident: `unknown` never breaches and never recovers.
 */
export const CHECK_STATUSES = ["pass", "breach", "training", "paused", "unknown"] as const;
export type CheckStatus = (typeof CHECK_STATUSES)[number];

/** The unit of an observed value. `count` is a number of skipped firings. */
export const OBSERVATION_UNITS = ["hours", "rows", "minutes", "count"] as const;
export type ObservationUnit = (typeof OBSERVATION_UNITS)[number];

export const CHECK_UNIT_OF = {
  freshness: "hours",
  overdue_run: "minutes",
  zero_rows: "rows",
  volume: "rows",
  duration: "minutes",
  schema_change: "count",
  table_missing: "count",
  // The failed runs in a row.
  failed_run: "count",
} as const satisfies Record<CheckKind, ObservationUnit>;

export const INCIDENT_STATUSES = ["open", "acknowledged", "resolved", "dismissed"] as const;
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];

/**
 * What an incident sends (spec section 7c): one event when it opens, one when a warning becomes a
 * failure, one when it closes. `reminder` is the second and last warning of a paused table,
 * 24 hours after the first (owner decision WD-1). `unjudgeable` (round 7, owner 2026-10-03,
 * instance 0021) closes an incident whose check can no longer judge (it lost its limit): not a
 * recovery, so it has its own words, and it reaches whoever the opening alert reached.
 */
export const MONITOR_TRANSITIONS = [
  "opened",
  "escalated",
  "reminder",
  "recovered",
  "unjudgeable",
] as const;
export type MonitorTransition = (typeof MONITOR_TRANSITIONS)[number];

/**
 * Round 7 (owner 2026-10-03, instance 0021 `close_reason`): why a check can no longer judge, kept
 * on the incident it closed and carried by the closing update. Codes, never free text (SEC-17).
 * - `no_schedule`: a freshness check whose table no enabled schedule imports, with no fixed limit.
 * - `connection_disabled`: the table's connection is disabled (or no longer Ready).
 * - `connection_deleted`: the table's connection was deleted (final review B2).
 * - `schedule_disabled`, `schedule_deleted`: a schedule check's schedule.
 * - `table_deselected`: the table is no longer selected on its connection.
 * - `check_turned_off`: a person turned the check off (the web's PATCH `enabled: false`).
 * - `marked_not_an_issue`: a person chose "Not an issue" (the web's dismiss). Neither person code
 *   names the person: no incident or audit row carries a display name.
 */
export const INCIDENT_CLOSE_REASONS = [
  "no_schedule",
  "connection_disabled",
  "connection_deleted",
  "schedule_disabled",
  "schedule_deleted",
  "table_deselected",
  "check_turned_off",
  "marked_not_an_issue",
  // Round 9 (B-25): the instance's destination records no load skyMonitor can read.
  "no_destination",
  "destination_unsupported",
] as const;
export type IncidentCloseReason = (typeof INCIDENT_CLOSE_REASONS)[number];

/** Each reason as a sentence, for the closing update and the Incidents tab. */
export const INCIDENT_CLOSE_REASON_TEXT: Record<IncidentCloseReason, string> = {
  no_schedule: "No schedule imports this table any more and it has no fixed freshness limit.",
  connection_disabled: "The connection is disabled.",
  connection_deleted: "The connection was deleted.",
  schedule_disabled: "The schedule is disabled.",
  schedule_deleted: "The schedule was deleted.",
  table_deselected: "The table is no longer selected on its connection.",
  check_turned_off: "A person turned this check off.",
  marked_not_an_issue: "A person marked this as not an issue.",
  no_destination: "This instance has no warehouse destination yet, so no load is recorded.",
  destination_unsupported:
    "This instance's destination does not record the loads skyMonitor reads yet.",
};

/** A paused table warns this many times, 24 hours apart, then stays quiet (WD-1). */
export const PAUSED_WARNINGS_MAX = 2;
export const PAUSED_WARNING_GAP_HOURS = 24;

/** "Not an issue" closes the incident and mutes the check this long (spec section 7c). */
export const DISMISS_MUTE_DAYS = 7;

/** Observations are purged after this many days (spec section 9a). */
export const OBSERVATION_RETENTION_DAYS = 90;

/** The check detail route returns this many days of observations. */
export const DETAIL_OBSERVATION_DAYS = 30;

// ---------------------------------------------------------------------------------------------
// Parameters, one zod schema per kind (spec section 7b). Every field has a default, so `{}` is a
// default check's parameters and discovery stores exactly that.
// ---------------------------------------------------------------------------------------------

export const freshnessParamsSchema = z
  .object({
    /** A fixed limit in hours; null (the default) derives it from the owning schedules. */
    maxAgeHours: z
      .number()
      .positive()
      .max(24 * 90)
      .nullable()
      .default(null),
    /** How long after a firing its load may still be running before the table counts as late. */
    graceHours: z.number().min(0).max(48).default(2),
  })
  .strict();

export const overdueRunParamsSchema = z
  .object({
    graceMinutes: z
      .number()
      .int()
      .min(1)
      .max(24 * 60)
      .default(10),
    /** Consecutive `previous_run_running` skips that warn. */
    skipsToWarn: z.number().int().min(1).max(20).default(2),
  })
  .strict();

export const zeroRowsParamsSchema = z
  .object({
    lookback: z.number().int().min(1).max(100).default(14),
    minShareWithRows: z.number().min(0).max(1).default(0.8),
  })
  .strict();

export const volumeParamsSchema = z
  .object({
    lookback: z.number().int().min(3).max(100).default(14),
    minHistory: z.number().int().min(3).max(100).default(7),
    sensitivity: z.number().positive().max(20).default(3),
    direction: z.enum(["drop", "both"]).default("drop"),
    /** An incremental table whose total falls by more than this between two loads breaches. */
    maxTotalDropPct: z.number().min(0).max(100).default(10),
  })
  .strict();

export const durationParamsSchema = z
  .object({
    lookback: z.number().int().min(3).max(100).default(14),
    minHistory: z.number().int().min(3).max(100).default(7),
    factor: z.number().min(1).max(20).default(2),
    floorMinutes: z
      .number()
      .min(0)
      .max(24 * 60)
      .default(30),
  })
  .strict();

/** `failed_run`'s default lookback: a gap this long with no finished run ends an episode. */
export const FAILED_RUN_LOOKBACK_DAYS_DEFAULT = 30;

/**
 * `failed_run` (#829): the newest finished run of the schedule in the last `lookbackDays`; none is
 * `unknown`.
 */
export const failedRunParamsSchema = z
  .object({
    lookbackDays: z.number().int().min(1).max(90).default(FAILED_RUN_LOOKBACK_DAYS_DEFAULT),
  })
  .strict();

const noParamsSchema = z.object({}).strict();

export const CHECK_PARAMS_SCHEMAS = {
  freshness: freshnessParamsSchema,
  overdue_run: overdueRunParamsSchema,
  zero_rows: zeroRowsParamsSchema,
  volume: volumeParamsSchema,
  duration: durationParamsSchema,
  schema_change: z.object({ notify: z.boolean().default(false) }).strict(),
  table_missing: noParamsSchema,
  failed_run: failedRunParamsSchema,
} as const satisfies Record<CheckKind, z.ZodType>;

export type FreshnessParams = z.infer<typeof freshnessParamsSchema>;
export type OverdueRunParams = z.infer<typeof overdueRunParamsSchema>;
export type ZeroRowsParams = z.infer<typeof zeroRowsParamsSchema>;
export type VolumeParams = z.infer<typeof volumeParamsSchema>;
export type DurationParams = z.infer<typeof durationParamsSchema>;
export type FailedRunParams = z.infer<typeof failedRunParamsSchema>;

// ---------------------------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------------------------

export const CHECK_KIND_LABELS = {
  freshness: "Freshness",
  overdue_run: "Overdue run",
  zero_rows: "Zero rows",
  volume: "Row volume",
  duration: "Run duration",
  schema_change: "Schema change",
  table_missing: "Table missing",
  failed_run: "Failed run",
} as const satisfies Record<CheckKind, string>;

/**
 * The status a screen shows, colour and label together (never colour alone): a breach reads by
 * its incident's severity, a muted check says so whatever it found.
 */
export const DISPLAY_STATUSES = [
  "healthy",
  "warning",
  "failing",
  "training",
  "paused",
  "muted",
  "unknown",
] as const;
export type DisplayStatus = (typeof DISPLAY_STATUSES)[number];

export const DISPLAY_STATUS_LABELS = {
  healthy: "Healthy",
  warning: "Warning",
  failing: "Failing",
  training: "Training",
  paused: "Paused",
  muted: "Muted",
  unknown: "Unknown",
} as const satisfies Record<DisplayStatus, string>;

/** How bad a display status is, for "the worst monitor of a table". */
export const DISPLAY_STATUS_RANK = {
  failing: 6,
  warning: 5,
  paused: 4,
  unknown: 3,
  training: 2,
  muted: 1,
  healthy: 0,
} as const satisfies Record<DisplayStatus, number>;

/**
 * The display status of one check: muted first (a muted check is silent whatever it found), then
 * a breach by its severity (the incident's when there is one), then what the evaluation said.
 * A check never evaluated reads `unknown`.
 */
export function displayStatusOf(input: {
  enabled: boolean;
  lastStatus: CheckStatus | null;
  severity: CheckSeverity;
  incidentSeverity: CheckSeverity | null;
  mutedUntil: Date | null;
  now: Date;
}): DisplayStatus {
  if (!input.enabled) return "muted";
  if (input.mutedUntil && input.mutedUntil.getTime() > input.now.getTime()) return "muted";
  switch (input.lastStatus) {
    case "breach":
      return (input.incidentSeverity ?? input.severity) === "failure" ? "failing" : "warning";
    case "pass":
      return "healthy";
    case "training":
      return "training";
    case "paused":
      return input.incidentSeverity ? "warning" : "paused";
    default:
      return "unknown";
  }
}

/** The worst of several statuses; `healthy` for none. */
export function worstStatus(statuses: readonly DisplayStatus[]): DisplayStatus {
  let worst: DisplayStatus = "healthy";
  for (const status of statuses) {
    if (DISPLAY_STATUS_RANK[status] > DISPLAY_STATUS_RANK[worst]) worst = status;
  }
  return worst;
}

/** The outcome, and so the rule cell ("When a monitor ..."), of each transition. */
export function transitionOutcome(
  transition: MonitorTransition,
  severity: CheckSeverity,
): "failure" | "warning" | "success" {
  if (transition === "recovered") return "success";
  if (transition === "escalated") return "failure";
  if (transition === "reminder") return "warning";
  // `unjudgeable` (round 7): the incident's own severity, so the closing update is routed by the
  // same cell as the alert it closes and reaches the same people.
  return severity;
}

// ---------------------------------------------------------------------------------------------
// Notification facts (spec section 10): counts and identifiers only, never a table name
// ---------------------------------------------------------------------------------------------

/** #1221: what a volume check's value counts: the rows a load brought, or the rows in total. */
export const VOLUME_BASES = ["loaded", "total"] as const;
export type VolumeBasis = (typeof VOLUME_BASES)[number];

/**
 * What a `monitor` event says: the check kind, the transition, how many tables (or schedules)
 * it covers, the observed value and the expected range as numbers. The connection's or the
 * schedule's NAME travels in the facts' own `connectionNames` / `scheduleName`; a table name
 * never does (SEC-17), so the strict object has no field for one.
 */
export const monitorFactsSchema = z
  .object({
    checkKind: z.enum(CHECK_KINDS),
    transition: z.enum(MONITOR_TRANSITIONS),
    /** Tables for a table check, schedules for a schedule check. */
    tablesAffected: z.number().int().nonnegative(),
    observed: z.number().finite(),
    expectedLow: z.number().finite().optional(),
    expectedHigh: z.number().finite().optional(),
    unit: z.enum(OBSERVATION_UNITS),
    /** True for a paused table's warning (WD-1). */
    paused: z.boolean().optional(),
    /** Round 7: on an `unjudgeable` close, why the check can no longer judge (a code). */
    closeReason: z.enum(INCIDENT_CLOSE_REASONS).optional(),
    /**
     * #829 (R6.5): on a `failed_run` alert, who acts on the failed run's cause (`whoActs`), so the
     * message says "Skypoint is looking into it" for a cause on Skypoint's side. A closed word.
     */
    whoActs: z.enum(WHO_ACTS).optional(),
    /**
     * #839: on a `failed_run` alert, the run it judged, on the run page
     * (`instanceScreenUrl(origin, instance, "/runs/<id>")`), beside the event's skyMonitor link.
     */
    runLink: httpLinkSchema.optional(),
    /**
     * #1280: on a `volume` alert, what `observed` counts, the rows a load brought (`loaded`) or
     * the rows the table holds (`total`), the same rule as the Tables tab. Absent when the load
     * cannot be matched, or the tables of the group differ: the message then names no basis.
     */
    volumeBasis: z.enum(VOLUME_BASES).optional(),
    /**
     * #1279 review: on a `zero_rows` alert, how many of its tables a full refresh left empty (the
     * check's failure, `emptiedTable`), so the message says the table is now empty instead of the
     * warning's "no new data". Absent when none is.
     */
    emptiedTables: z.number().int().positive().optional(),
  })
  .strict();
export type MonitorFacts = z.infer<typeof monitorFactsSchema>;

// ---------------------------------------------------------------------------------------------
// Views and bodies (spec section 9d)
// ---------------------------------------------------------------------------------------------

export const monitorSummarySchema = z.object({
  /** The platform flag. Off: the page says skyMonitor is off and shows nothing else. */
  enabled: z.boolean(),
  tables: z.object({
    watched: z.number().int(),
    healthy: z.number().int(),
    failing: z.number().int(),
    warning: z.number().int(),
    training: z.number().int(),
    paused: z.number().int(),
    muted: z.number().int(),
    unknown: z.number().int(),
  }),
  schedules: z.object({
    watched: z.number().int(),
    failing: z.number().int(),
    warning: z.number().int(),
  }),
  openIncidents: z.number().int(),
  /**
   * Round 6 W15: the watched tables and schedules that are failing or warning, or hold an open or
   * acknowledged incident (one whose check reads Unknown, Training or Muted since is still on the
   * Incidents tab). The Overview tile names them, so it agrees with that tab.
   */
  attention: z.object({ tables: z.number().int(), schedules: z.number().int() }),
  /** `attention.tables` plus `attention.schedules`: the Overview tile's number. */
  needAttention: z.number().int(),
  lastEvaluatedAt: z.string().nullable(),
  /** The newest skyMonitor heartbeat row; null when no skyMonitor has ever reported. */
  monitorLastSeenAt: z.string().nullable(),
  /** No heartbeat for `MONITOR_HEARTBEAT_STALE_MINUTES`, or none ever: the page warns. */
  monitorStale: z.boolean(),
});
export type MonitorSummary = z.infer<typeof monitorSummarySchema>;

export const monitorCheckRowSchema = z.object({
  id: z.uuid(),
  kind: z.enum(CHECK_KINDS),
  scope: z.enum(CHECK_SCOPES),
  dataflowId: z.uuid().nullable(),
  /** The connection's display name, or its name; null for a schedule check or a deleted one. */
  connectionName: z.string().nullable(),
  /** The table (entity) name: shown in the product, never in a notification. */
  entityName: z.string().nullable(),
  /**
   * Round 6 W15: the warehouse table the entity last landed in where the binding points now, as
   * the Explorer lists it (the load's recorded `targetTable`); null for a schedule check, or
   * before the table landed there.
   */
  tableName: z.string().nullable(),
  scheduleId: z.uuid().nullable(),
  scheduleName: z.string().nullable(),
  enabled: z.boolean(),
  severity: z.enum(CHECK_SEVERITIES),
  source: z.enum(CHECK_SOURCES),
  status: z.enum(DISPLAY_STATUSES),
  lastStatus: z.enum(CHECK_STATUSES).nullable(),
  lastValue: z.number().nullable(),
  expectedLow: z.number().nullable(),
  expectedHigh: z.number().nullable(),
  unit: z.enum(OBSERVATION_UNITS),
  /** The signal's own time: the table's last load, or the schedule's last run start. */
  signalAt: z.string().nullable(),
  lastEvalAt: z.string().nullable(),
  mutedUntil: z.string().nullable(),
  /** The check's open or acknowledged incident, if any. */
  incidentId: z.uuid().nullable(),
  /**
   * Round 10 wave 2 (A2b): on an `overdue_run` check in breach by skipped firings (unit `count`),
   * why the newest whole firing was skipped and, for `connection_busy`, the schedule whose run held
   * the connection (null: an import started by hand). Absent on every other row.
   */
  skipDetail: z
    .object({
      reason: z.enum(["previous_run_running", "connection_busy"]),
      holderScheduleName: z.string().nullable(),
      count: z.number().int().nonnegative(),
    })
    .optional(),
  /**
   * #1221: on a `volume` check, the two counts of the load it judged (the load step that finished
   * at `signalAt`, from skyData's own records, ADR 0011) and which one `lastValue` and the usual
   * range are about: `loaded`, the rows the load brought (an incremental table), or `total`, the
   * rows the table holds after it (a full refresh, a forced full refresh, incremental off, or an
   * incremental total that fell). Each is null where that load is not found or recorded no such
   * count. Absent on every other row.
   */
  loadCounts: z
    .object({
      basis: z.enum(VOLUME_BASES).nullable(),
      lastLoadRows: z.number().nonnegative().nullable(),
      totalRecords: z.number().nonnegative().nullable(),
    })
    .strict()
    .optional(),
});
export type MonitorCheckRow = z.infer<typeof monitorCheckRowSchema>;

export const monitorObservationSchema = z.object({
  observedAt: z.string(),
  value: z.number().nullable(),
  expectedLow: z.number().nullable(),
  expectedHigh: z.number().nullable(),
  status: z.enum(CHECK_STATUSES),
  runId: z.string().nullable(),
  /**
   * #1281: on a Row volume or Zero rows observation, which count its value is: the rows the load
   * brought or the rows the table held after it, read from that run's load record. Null when the
   * run's record does not say; absent on any other kind.
   */
  basis: z.enum(VOLUME_BASES).nullable().optional(),
});
export type MonitorObservation = z.infer<typeof monitorObservationSchema>;

export const monitorCheckDetailSchema = z.object({
  check: monitorCheckRowSchema,
  /** The check's parameters as stored; `{}` is every default. */
  params: z.record(z.string(), z.unknown()),
  /** The last `DETAIL_OBSERVATION_DAYS` days, oldest first. */
  observations: z.array(monitorObservationSchema),
});
export type MonitorCheckDetail = z.infer<typeof monitorCheckDetailSchema>;

export const monitorIncidentRowSchema = z.object({
  id: z.uuid(),
  checkId: z.uuid(),
  kind: z.enum(CHECK_KINDS),
  dataflowId: z.uuid().nullable(),
  connectionName: z.string().nullable(),
  entityName: z.string().nullable(),
  /** Round 6 W15: as on a check row, the Explorer's name for the table. */
  tableName: z.string().nullable(),
  scheduleId: z.uuid().nullable(),
  scheduleName: z.string().nullable(),
  severity: z.enum(CHECK_SEVERITIES),
  status: z.enum(INCIDENT_STATUSES),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
  /** The user id of whoever chose "Assign to me"; null while nobody has. */
  acknowledgedBy: z.string().nullable(),
  acknowledgedByMe: z.boolean(),
  lastValue: z.number().nullable(),
  unit: z.enum(OBSERVATION_UNITS),
  /** Round 7: why a closed incident's check can no longer judge; null otherwise. */
  closeReason: z.enum(INCIDENT_CLOSE_REASONS).nullable(),
  /**
   * #1281: on a Row volume or Zero rows incident, which count `lastValue` is, read from the run of
   * the check's newest observation of that value. Null when it cannot be told; absent otherwise.
   */
  basis: z.enum(VOLUME_BASES).nullable().optional(),
});
export type MonitorIncidentRow = z.infer<typeof monitorIncidentRowSchema>;

export const monitorChecksQuerySchema = z.object({
  dataflowId: z.uuid().optional(),
  scheduleId: z.uuid().optional(),
  status: z.enum(DISPLAY_STATUSES).optional(),
  kind: z.enum(CHECK_KINDS).optional(),
});
export type MonitorChecksQuery = z.infer<typeof monitorChecksQuerySchema>;

export const monitorIncidentsQuerySchema = z.object({
  /** `open` (the default) lists open and acknowledged incidents; `closed` the last 30 days' others. */
  status: z.enum(["open", "closed"]).default("open"),
});
export type MonitorIncidentsQuery = z.infer<typeof monitorIncidentsQuerySchema>;

export const monitorIdParamsSchema = z.object({ id: z.uuid() });

/**
 * `PATCH /monitor/checks/:id` (spec section 9d): switch a check on or off, change its severity or
 * parameters, mute it until a time, or reset it to skyMonitor's default. Any edit but the reset
 * turns the check `custom`, which discovery never overwrites.
 */
export const patchMonitorCheckBodySchema = z
  .object({
    enabled: z.boolean().optional(),
    severity: z.enum(CHECK_SEVERITIES).optional(),
    /** Validated against the check's kind by the server. */
    params: z.record(z.string(), z.unknown()).optional(),
    /** An ISO time in the future, or null to unmute. */
    mutedUntil: z.iso.datetime({ offset: true }).nullable().optional(),
    resetToDefault: z.literal(true).optional(),
  })
  .strict()
  .refine((body) => Object.keys(body).length > 0, "Change at least one thing.")
  .refine(
    (body) => !body.resetToDefault || Object.keys(body).length === 1,
    "Reset to default alone: it replaces every other setting.",
  );
export type PatchMonitorCheckBody = z.infer<typeof patchMonitorCheckBodySchema>;

/** The empty state of the Tables tab (spec section 11). */
export const MONITOR_EMPTY_TABLES = "No table has loaded yet, so there is nothing to watch.";

/**
 * The Overview tile's sentence: the tables and (round 6 W15) the schedules that need attention
 * (`MonitorSummary.attention`).
 */
export function needAttentionSentence(tables: number, schedules = 0): string {
  if (tables === 0 && schedules === 0) return "Data health: every watched table is healthy";
  const tablePart = `${tables} ${tables === 1 ? "table" : "tables"}`;
  const schedulePart = `${schedules} ${schedules === 1 ? "schedule" : "schedules"}`;
  if (schedules === 0) {
    return `Data health: ${tablePart} ${tables === 1 ? "needs" : "need"} attention`;
  }
  if (tables === 0) {
    return `Data health: ${schedulePart} ${schedules === 1 ? "needs" : "need"} attention`;
  }
  return `Data health: ${tablePart} and ${schedulePart} need attention`;
}

/** Minutes of silence after which the skyMonitor page says skyMonitor is not reporting. */
export const MONITOR_HEARTBEAT_STALE_MINUTES = 10;

// ---------------------------------------------------------------------------------------------
// #842: a failed run and the schedule's open `failed_run` alert, linked both ways
// ---------------------------------------------------------------------------------------------

/**
 * A `monitor` event's `subject_key`: `monitor:<kind>:<connection or schedule id>`, within
 * `varchar(80)`. skyMonitor writes it (`monitor/notify.ts`); the runner's expansion looks a
 * schedule's `failed_run` alert up by it (#842), so both build it here.
 */
export function monitorSubjectKeyOf(kind: CheckKind, targetId: string): string {
  return `monitor:${kind}:${targetId}`;
}

/** The transitions after which an incident is still open: anything but a close. */
export const OPEN_ALERT_TRANSITIONS = [
  "opened",
  "escalated",
  "reminder",
] as const satisfies readonly MonitorTransition[];

/** skyMonitor's Incidents tab, the path an "Open the alert" link opens (#842). */
export const MONITOR_INCIDENTS_PATH = "/monitor?tab=incidents";

/**
 * `GET /monitor/runs/:runId/incident` (#842): the schedule's open `failed_run` incident, only for
 * a failed or partly failed run of the episode it covers; null otherwise, and null while
 * skyMonitor is off.
 */
export const runIncidentViewSchema = z
  .object({
    incident: z
      .object({
        id: z.uuid(),
        status: z.enum(INCIDENT_STATUSES),
        openedAt: z.string(),
        scheduleName: z.string().nullable(),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type RunIncidentView = z.infer<typeof runIncidentViewSchema>;

export const runIncidentParamsSchema = z.object({ runId: z.string().min(1).max(64) });
